import { useEffect, useState } from 'react';
import { Box } from '@mui/system';
import { Paper, Typography } from '@mui/material'
import { Timeline, TimelineConnector, TimelineContent, TimelineDot, TimelineItem, TimelineOppositeContent, TimelineSeparator } from '@mui/lab'

import CreationIcon from '@mui/icons-material/Assistant';
import ChangeTextIcon from '@mui/icons-material/Article';
import ChangeTopicIcon from '@mui/icons-material/TextFields';
import ChangeTypeIcon from '@mui/icons-material/People';
import ChangeManyIcon from '@mui/icons-material/Update';
import RemovalIcon from '@mui/icons-material/CheckCircle';

import { useAuth } from '../components/AuthContext';
import ErrorPaper from '../components/ErrorPaper';


const Styles = ({
  paper: {
      maxWidth: { xl: 1240, lg: 940, md: 640, sm: 480, xs: 320 },
      width: { xl: 1200, lg: 900, md: 600, sm: 480, xs: 320 },
      margin: '20px auto',
      overflow: 'hidden',
  },
  inner: {
      padding: '10px 15px 10px 15px',
  },
});

const PensieveBaseUri = 'http://api.epensieve.com';

type Thought = {
  topic: string,
  text: string,
  type: string,
}

const getType = (type: string) => {
  switch (type) {
    case 'personal':
      return 'Personal';
    case 'professional':
      return 'Professional';
    case 'social':
      return 'Social';
    default:
      return '';
  }
}

const EventUI = (thought: Thought) => ({
  'creation': {
    title: `New ${getType(thought.type)} Thought on ${thought.topic}`,
    icon: <CreationIcon />,
    body: thought.text,
  },
  'change.text': {
    title: `Changed Text of a ${getType(thought.type)} Thought on ${thought.topic}`,
    icon: <ChangeTextIcon />,
    body: thought.text,
  },
  'change.topic': {
    title: `Changed Topic of a ${getType(thought.type)} Thought to ${thought.topic}`,
    icon: <ChangeTopicIcon />,
    body: thought.text,
  },
  'change.type': {
    title: `Changed to ${getType(thought.type)} a Thought on ${thought.topic}`,
    icon: <ChangeTypeIcon />,
    body: thought.text,
  },
  'change.many': {
    title: `Changed a ${getType(thought.type)} Thought on ${thought.topic}`,
    icon: <ChangeManyIcon />,
    body: thought.text,
  },
  'removal': {
    title: `Finished a ${getType(thought.type)} Thought on ${thought.topic}`,
    icon: <RemovalIcon />,
    body: thought.text,
  },
})


function TimelineViewer() {
  const {userId, token} = useAuth()!;
  const [loading, setLoading] = useState(true);
  const [events, setEvents] = useState<any[]>(null!);
  const [error, setError] = useState<string | null>(null);
  
  useEffect(() => {
    let url = `${PensieveBaseUri}/timelines/${(userId!)}`;
    fetch(url, {method: 'GET',
      headers: {
        'Authorization': `Bearer ${token}`
      }
    })
    .then( (response) => {
        if (!response.ok) {
            throw new Error('Issue retrieving the detailed list of Topics');
        }

        return response.json();
    })
    .then((result) => {
        setEvents(result.timeline);
        setLoading(false);
    })
    .catch((err) => {
        setError(err.message);
    })
  }, [userId]);

  if (loading) {
    return (<Paper sx={Styles.paper} > <Typography> Loading Topics ... </Typography> </Paper>);
  }

  return (
    <Box>
      { (error) && <ErrorPaper error={error} /> }
    
      <Paper sx={{...Styles.paper, paddingTop:'25px'}} >
        <Timeline position="alternate">
          {(
            events
              .map((event) => ({ type: event.event, date: event.date, ui: EventUI(JSON.parse(event.text)) }))
              .map((event : {type: string, date: string, ui: any}) => (
              <TimelineItem>
                <TimelineOppositeContent
                  sx={{ m: 'auto 0' }}
                  align="right"
                  variant="body2"
                  color="text.secondary"
                >
                  {new Date(event.date).toLocaleString()}
                </TimelineOppositeContent>
                <TimelineSeparator>
                  <TimelineConnector />
                  <TimelineDot color="primary">
                    { event.ui[event.type].icon }
                  </TimelineDot>
                  <TimelineConnector />
                </TimelineSeparator>
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Paper sx={Styles.inner} >
                    <Typography variant="h6" component="span">
                      { event.ui[event.type].title }
                    </Typography>
                    <Typography>{ event.ui[event.type].body }</Typography>
                  </Paper>
                </TimelineContent>
              </TimelineItem>
            ))
          )}
          <TimelineItem>
            <TimelineOppositeContent />
            <TimelineSeparator>
              <TimelineConnector />
              <TimelineDot color="primary" />
            </TimelineSeparator>
            <TimelineContent />
          </TimelineItem>
        </Timeline>
      </Paper>
    </Box>
  )
}

export default TimelineViewer